const SLIDE_DURATION_MS = 2800;
const FRAME_RATE = 24;

const MEDIA_RECORDER_TYPES = [
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

const loadImages = async (files) => {
  const urls = files.map((file) => URL.createObjectURL(file));
  try {
    return await Promise.all(
      urls.map(
        (url) =>
          new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () =>
              reject(new Error("A selected profile photo could not be read."));
            image.src = url;
          }),
      ),
    );
  } catch (error) {
    urls.forEach((url) => URL.revokeObjectURL(url));
    throw error;
  }
};

const drawPhoto = (context, image, progress, previousImage) => {
  const { canvas } = context;
  const width = canvas.width;
  const height = canvas.height;
  const scale = 0.94 + progress * 0.06;

  context.save();
  context.filter = "blur(18px) brightness(0.6)";
  context.globalAlpha = 0.45;
  context.drawImage(image, 0, 0, width, height);
  context.restore();
  context.filter = "none";
  context.fillStyle = "rgba(255, 255, 255, 0.45)";
  context.fillRect(0, 0, width, height);

  if (previousImage && progress < 0.2) {
    context.globalAlpha = 1 - progress / 0.2;
    context.drawImage(previousImage, 0, 0, width, height);
    context.globalAlpha = 1;
  }

  const fitScale = Math.min(
    (width * 0.82) / image.width,
    (height * 0.82) / image.height,
  );
  const imageWidth = image.width * fitScale * scale;
  const imageHeight = image.height * fitScale * scale;
  context.drawImage(
    image,
    (width - imageWidth) / 2,
    (height - imageHeight) / 2,
    imageWidth,
    imageHeight,
  );
};

const playGentleMusic = (audioContext, destination, durationSeconds) => {
  const masterGain = audioContext.createGain();
  masterGain.gain.value = 0.4;
  masterGain.connect(destination);

  const chords = [
    [261.63, 329.63, 392],
    [220, 261.63, 329.63],
    [174.61, 220, 261.63],
    [196, 246.94, 293.66],
  ];
  const chordDuration = 2.8;
  const startTime = audioContext.currentTime + 0.05;

  for (let index = 0; index * chordDuration < durationSeconds; index += 1) {
    const chord = chords[index % chords.length];
    const chordStart = startTime + index * chordDuration;

    chord.forEach((frequency, noteIndex) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const noteStart = chordStart + noteIndex * 0.16;
      const noteEnd = Math.min(noteStart + 1.8, startTime + durationSeconds);

      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.linearRampToValueAtTime(0.18, noteStart + 0.12);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);
      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(noteStart);
      oscillator.stop(noteEnd);
    });

    const melodyNotes = [
      chord[1] * 2,
      chord[2] * 2,
      chord[1] * 2,
      chord[0] * 2,
    ];
    melodyNotes.forEach((frequency, noteIndex) => {
      const noteStart = chordStart + noteIndex * 0.62 + 0.2;
      if (noteStart >= startTime + durationSeconds) return;

      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const noteEnd = Math.min(noteStart + 0.48, startTime + durationSeconds);

      oscillator.type = "triangle";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.linearRampToValueAtTime(0.16, noteStart + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);
      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(noteStart);
      oscillator.stop(noteEnd);
    });
  }
};

const waitForSlide = (drawFrame, signal) =>
  new Promise((resolve, reject) => {
    const startTime = performance.now();
    const draw = (time) => {
      if (signal?.aborted) {
        reject(
          new DOMException("Video generation was cancelled.", "AbortError"),
        );
        return;
      }

      const progress = Math.min((time - startTime) / SLIDE_DURATION_MS, 1);
      drawFrame(progress);
      if (progress === 1) {
        resolve();
      } else {
        requestAnimationFrame(draw);
      }
    };

    requestAnimationFrame(draw);
  });

const resumeAudioContext = async (audioContext) => {
  let timeoutId;
  try {
    await Promise.race([
      audioContext.resume(),
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(
          () =>
            reject(
              new Error("The browser did not allow background music to start."),
            ),
          2000,
        );
      }),
    ]);
    return audioContext.state === "running";
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const createPhotoIntroductionVideo = async (files, { signal } = {}) => {
  const imageFiles = files.filter(
    (file) => file instanceof File && file.type.startsWith("image/"),
  );
  if (!imageFiles.length) {
    throw new Error("Upload at least one profile photo to create a video.");
  }
  if (!window.MediaRecorder || !HTMLCanvasElement.prototype.captureStream) {
    throw new Error(
      "Automatic video creation is not supported in this browser. Please upload an introduction video instead.",
    );
  }
  if (signal?.aborted) {
    throw new DOMException("Video generation was cancelled.", "AbortError");
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  let audioContext;
  let photoUrls = [];
  let mediaStream;
  let recorder;
  let recordingPromise;

  try {
    let audioDestination;
    if (AudioContextClass) {
      audioContext = new AudioContextClass();
      if (await resumeAudioContext(audioContext)) {
        audioDestination = audioContext.createMediaStreamDestination();
      } else {
        await audioContext.close();
        audioContext = undefined;
      }
    }

    const images = await loadImages(imageFiles);
    photoUrls = images.map((image) => image.src);

    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not prepare the introduction video.");
    }

    const durationSeconds = (SLIDE_DURATION_MS * imageFiles.length) / 1000;
    if (audioContext && audioDestination) {
      playGentleMusic(audioContext, audioDestination, durationSeconds);
    }

    const videoStream = canvas.captureStream(FRAME_RATE);
    mediaStream = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...(audioDestination?.stream.getAudioTracks() || []),
    ]);

    const mimeType = MEDIA_RECORDER_TYPES.find(
      (type) =>
        typeof MediaRecorder.isTypeSupported !== "function" ||
        MediaRecorder.isTypeSupported(type),
    );
    recorder = mimeType
      ? new MediaRecorder(mediaStream, {
          mimeType,
          videoBitsPerSecond: 1_000_000,
          audioBitsPerSecond: 96_000,
        })
      : new MediaRecorder(mediaStream);

    const chunks = [];
    let recordingError;
    recordingPromise = new Promise((resolve, reject) => {
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onerror = () => {
        recordingError = new Error(
          "The introduction video could not be recorded.",
        );
        if (recorder.state !== "inactive") recorder.stop();
      };
      recorder.onstop = () => {
        if (recordingError) {
          reject(recordingError);
          return;
        }
        resolve(
          new Blob(chunks, {
            type: recorder.mimeType || mimeType || "video/webm",
          }),
        );
      };
    });
    recordingPromise.catch(() => {});

    recorder.start();
    drawPhoto(context, images[0], 0, null);
    for (let index = 0; index < images.length; index += 1) {
      const previousImage = images[index - 1];
      await waitForSlide(
        (progress) =>
          drawPhoto(context, images[index], progress, previousImage),
        signal,
      );
    }
    recorder.stop();

    const videoBlob = await recordingPromise;
    if (!videoBlob.size) {
      throw new Error("The generated introduction video is empty.");
    }

    const extension = videoBlob.type.includes("mp4") ? "mp4" : "webm";
    return new File([videoBlob], `profile-introduction.${extension}`, {
      type: videoBlob.type,
    });
  } catch (error) {
    if (recorder && recorder.state !== "inactive") recorder.stop();
    if (recordingPromise) await recordingPromise.catch(() => {});
    throw error;
  } finally {
    mediaStream?.getTracks().forEach((track) => track.stop());
    photoUrls.forEach((url) => URL.revokeObjectURL(url));
    if (audioContext && audioContext.state !== "closed") {
      await audioContext.close();
    }
  }
};

export const getIntroductionVideoForUpload = async ({
  videoFile,
  imageFiles,
  setVideoFile,
  setIsGenerating,
  onGenerationError,
}) => {
  if (videoFile || imageFiles.length === 0) return videoFile;

  setIsGenerating(true);
  try {
    const generatedVideo = await createPhotoIntroductionVideo(imageFiles);
    setVideoFile(generatedVideo);
    return generatedVideo;
  } catch (error) {
    if (!onGenerationError) throw error;
    onGenerationError(error);
    return null;
  } finally {
    setIsGenerating(false);
  }
};
